import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { KanbanDragHandleIcon } from '@/components/ui/KanbanDragHandleIcon';

/** Applied to body while dragging — must match {@link BlockerKanbanDragCursorStyle} in BlockersKanbanBoard. */
export const BLOCKER_KANBAN_DRAG_CLASS = 'blocker-kanban-dragging';

const KANBAN_SOURCE_SHRINK_DURATION = 0.58;
const KANBAN_SOURCE_SHRINK_EASE = [0.22, 1, 0.36, 1];
const KANBAN_FLOAT_EXIT_MS = 320;
const KANBAN_LONG_PRESS_MS = 450;
const KANBAN_LONG_PRESS_MOVE_CANCEL_PX = 10;

let transparentDragCanvas;
function getTransparentDragCanvas() {
    if (!transparentDragCanvas) {
        transparentDragCanvas = document.createElement('canvas');
        transparentDragCanvas.width = 1;
        transparentDragCanvas.height = 1;
        transparentDragCanvas.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
        document.body.appendChild(transparentDragCanvas);
    }
    return transparentDragCanvas;
}

/**
 * Drag wrapper for blocker Kanban cards (same UX as sprint {@link SprintKanbanBoard} TaskDragWrap).
 */
export function BlockerDragWrap({ sourceColumn, blockerId, onBlockerDragStart, onBlockerDragEnd, children }) {
    const wrapperRef = useRef(null);
    const floatRef = useRef(null);
    const dragMoveHandlerRef = useRef(null);
    const mountedRef = useRef(true);
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef(null);
    const lastPointerClientRef = useRef({ x: 0, y: 0 });
    const armPointerMoveHandlerRef = useRef(null);
    const armPointerFollowActiveRef = useRef(false);
    const armVisualAllowedRef = useRef(false);
    const longPressArmedRef = useRef(false);
    const suppressNextClickRef = useRef(false);
    const [shrinkSource, setShrinkSource] = useState(false);
    const [longPressArmed, setLongPressArmed] = useState(false);
    /** Keep wrapper `draggable` true until dragend — clearing long-press alone made draggable false mid-drag and broke dragend + float cleanup. */
    const [dragSessionActive, setDragSessionActive] = useState(false);
    const dragSessionActiveRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const clearLongPressTimer = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    }, []);

    const clearArmPointerFollow = useCallback(() => {
        if (armPointerMoveHandlerRef.current) {
            document.removeEventListener('pointermove', armPointerMoveHandlerRef.current);
            armPointerMoveHandlerRef.current = null;
        }
        armPointerFollowActiveRef.current = false;
    }, []);

    const cancelArmDragVisual = useCallback(() => {
        clearArmPointerFollow();
        document.body.classList.remove(BLOCKER_KANBAN_DRAG_CLASS);
        const floatEl = floatRef.current;
        floatRef.current = null;

        const finishShrink = () => {
            queueMicrotask(() => {
                if (mountedRef.current) setShrinkSource(false);
            });
        };

        if (floatEl) {
            floatEl
                .animate(
                    [
                        { opacity: 1, transform: 'scale(1)' },
                        { opacity: 0, transform: 'scale(0.97)' },
                    ],
                    {
                        duration: KANBAN_FLOAT_EXIT_MS,
                        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        fill: 'forwards',
                    },
                )
                .finished.then(() => {
                    try {
                        floatEl.remove();
                    } catch {
                        /* ignore */
                    }
                })
                .catch(() => {
                    try {
                        floatEl.remove();
                    } catch {
                        /* ignore */
                    }
                })
                .finally(() => {
                    finishShrink();
                });
        } else {
            finishShrink();
        }
    }, [clearArmPointerFollow]);

    const showArmDragVisual = useCallback(() => {
        if (!armVisualAllowedRef.current || !blockerId || dragSessionActiveRef.current) return;
        const el = wrapperRef.current;
        if (!el || floatRef.current) return;

        const rect = el.getBoundingClientRect();
        const lp = lastPointerClientRef.current;
        const grabX = lp.x - rect.left;
        const grabY = lp.y - rect.top;

        const floatEl = el.cloneNode(true);
        floatEl.classList.add('blocker-kanban-task-drag-float');
        Object.assign(floatEl.style, {
            position: 'fixed',
            left: `${rect.left}px`,
            top: `${rect.top}px`,
            width: `${rect.width}px`,
            maxWidth: `${rect.width}px`,
            boxSizing: 'border-box',
            pointerEvents: 'none',
            opacity: '1',
            zIndex: '2147483646',
            margin: '0',
            boxShadow: '0 22px 56px rgba(0, 0, 0, 0.18)',
            borderRadius: '12px',
        });
        document.body.appendChild(floatEl);
        floatRef.current = floatEl;
        document.body.classList.add(BLOCKER_KANBAN_DRAG_CLASS);

        const onArmPointerMove = (ev) => {
            if (!floatRef.current || dragSessionActiveRef.current) return;
            floatRef.current.style.left = `${ev.clientX - grabX}px`;
            floatRef.current.style.top = `${ev.clientY - grabY}px`;
        };
        armPointerMoveHandlerRef.current = onArmPointerMove;
        armPointerFollowActiveRef.current = true;
        document.addEventListener('pointermove', onArmPointerMove, { passive: true });
    }, [blockerId]);

    const runDragStart = useCallback(
        (e) => {
            if (!blockerId) {
                e.preventDefault();
                return;
            }
            const el = wrapperRef.current;
            if (!el) {
                e.preventDefault();
                return;
            }

            const hadArmPreview = armPointerFollowActiveRef.current;
            clearArmPointerFollow();

            if (floatRef.current && hadArmPreview) {
                const fr = floatRef.current.getBoundingClientRect();
                const offsetX = e.clientX - fr.left;
                const offsetY = e.clientY - fr.top;

                if (dragMoveHandlerRef.current) {
                    document.removeEventListener('drag', dragMoveHandlerRef.current);
                    dragMoveHandlerRef.current = null;
                }

                try {
                    e.dataTransfer.setDragImage(getTransparentDragCanvas(), 0, 0);
                } catch {
                    /* Safari */
                }

                const onDragMove = (ev) => {
                    if (!floatRef.current) return;
                    if (ev.clientX === 0 && ev.clientY === 0) return;
                    floatRef.current.style.left = `${ev.clientX - offsetX}px`;
                    floatRef.current.style.top = `${ev.clientY - offsetY}px`;
                };
                dragMoveHandlerRef.current = onDragMove;
                document.addEventListener('drag', onDragMove);

                requestAnimationFrame(() => {
                    setShrinkSource(true);
                });
                onBlockerDragStart?.(sourceColumn);
                const payload = { blockerId, sourceColumn };
                const payloadStr = JSON.stringify(payload);
                e.dataTransfer.setData('application/json', payloadStr);
                e.dataTransfer.setData('text/plain', payloadStr);
                e.dataTransfer.effectAllowed = 'move';
                document.body.classList.add(BLOCKER_KANBAN_DRAG_CLASS);
                clearLongPressTimer();
                longPressArmedRef.current = false;
                setLongPressArmed(false);
                dragSessionActiveRef.current = true;
                setDragSessionActive(true);
                return;
            }

            if (floatRef.current) {
                try {
                    floatRef.current.remove();
                } catch {
                    /* ignore */
                }
                floatRef.current = null;
            }
            if (dragMoveHandlerRef.current) {
                document.removeEventListener('drag', dragMoveHandlerRef.current);
                dragMoveHandlerRef.current = null;
            }

            const rect = el.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;

            const floatEl = el.cloneNode(true);
            floatEl.classList.add('blocker-kanban-task-drag-float');
            Object.assign(floatEl.style, {
                position: 'fixed',
                left: `${rect.left}px`,
                top: `${rect.top}px`,
                width: `${rect.width}px`,
                maxWidth: `${rect.width}px`,
                boxSizing: 'border-box',
                pointerEvents: 'none',
                opacity: '1',
                zIndex: '2147483646',
                margin: '0',
                boxShadow: '0 22px 56px rgba(0, 0, 0, 0.18)',
                borderRadius: '12px',
            });
            document.body.appendChild(floatEl);
            floatRef.current = floatEl;

            try {
                e.dataTransfer.setDragImage(getTransparentDragCanvas(), 0, 0);
            } catch {
                /* Safari */
            }

            const onDragMove = (ev) => {
                if (!floatRef.current) return;
                if (ev.clientX === 0 && ev.clientY === 0) return;
                floatRef.current.style.left = `${ev.clientX - offsetX}px`;
                floatRef.current.style.top = `${ev.clientY - offsetY}px`;
            };
            dragMoveHandlerRef.current = onDragMove;
            document.addEventListener('drag', onDragMove);

            requestAnimationFrame(() => {
                setShrinkSource(true);
            });
            onBlockerDragStart?.(sourceColumn);
            const payload = { blockerId, sourceColumn };
            const payloadStr = JSON.stringify(payload);
            e.dataTransfer.setData('application/json', payloadStr);
            e.dataTransfer.setData('text/plain', payloadStr);
            e.dataTransfer.effectAllowed = 'move';
            document.body.classList.add(BLOCKER_KANBAN_DRAG_CLASS);
            clearLongPressTimer();
            longPressArmedRef.current = false;
            setLongPressArmed(false);
            dragSessionActiveRef.current = true;
            setDragSessionActive(true);
        },
        [blockerId, sourceColumn, onBlockerDragStart, clearLongPressTimer, clearArmPointerFollow],
    );

    const runDragEnd = useCallback(() => {
        dragSessionActiveRef.current = false;
        setDragSessionActive(false);
        clearArmPointerFollow();
        if (dragMoveHandlerRef.current) {
            document.removeEventListener('drag', dragMoveHandlerRef.current);
            dragMoveHandlerRef.current = null;
        }
        const floatEl = floatRef.current;
        floatRef.current = null;

        const finishShrink = () => {
            queueMicrotask(() => {
                if (mountedRef.current) setShrinkSource(false);
            });
        };

        if (floatEl) {
            floatEl
                .animate(
                    [
                        { opacity: 1, transform: 'scale(1)' },
                        { opacity: 0, transform: 'scale(0.97)' },
                    ],
                    {
                        duration: KANBAN_FLOAT_EXIT_MS,
                        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        fill: 'forwards',
                    },
                )
                .finished.then(() => {
                    try {
                        floatEl.remove();
                    } catch {
                        /* ignore */
                    }
                })
                .catch(() => {
                    try {
                        floatEl.remove();
                    } catch {
                        /* ignore */
                    }
                })
                .finally(() => {
                    finishShrink();
                });
        } else {
            finishShrink();
        }
        document.body.classList.remove(BLOCKER_KANBAN_DRAG_CLASS);
        armVisualAllowedRef.current = false;
        longPressArmedRef.current = false;
        setLongPressArmed(false);
        onBlockerDragEnd?.();
    }, [onBlockerDragEnd, clearArmPointerFollow]);

    const disarmLongPress = useCallback(() => {
        armVisualAllowedRef.current = false;
        clearLongPressTimer();
        longPressArmedRef.current = false;
        setLongPressArmed(false);
        if (!dragSessionActiveRef.current) {
            cancelArmDragVisual();
        }
    }, [clearLongPressTimer, cancelArmDragVisual]);

    const onWrapperPointerDown = useCallback(
        (e) => {
            if (e.button !== 0) return;
            if (e.target.closest?.('[data-kanban-drag-handle]')) return;
            lastPointerClientRef.current = { x: e.clientX, y: e.clientY };
            clearLongPressTimer();
            longPressStartRef.current = { x: e.clientX, y: e.clientY };
            longPressTimerRef.current = setTimeout(() => {
                longPressTimerRef.current = null;
                longPressStartRef.current = null;
                armVisualAllowedRef.current = true;
                longPressArmedRef.current = true;
                setLongPressArmed(true);
                queueMicrotask(() => {
                    showArmDragVisual();
                });
            }, KANBAN_LONG_PRESS_MS);
        },
        [clearLongPressTimer, showArmDragVisual],
    );

    const onWrapperPointerMove = useCallback(
        (e) => {
            lastPointerClientRef.current = { x: e.clientX, y: e.clientY };
            if (!longPressTimerRef.current || !longPressStartRef.current) return;
            const dx = Math.abs(e.clientX - longPressStartRef.current.x);
            const dy = Math.abs(e.clientY - longPressStartRef.current.y);
            if (dx > KANBAN_LONG_PRESS_MOVE_CANCEL_PX || dy > KANBAN_LONG_PRESS_MOVE_CANCEL_PX) {
                clearLongPressTimer();
            }
        },
        [clearLongPressTimer],
    );

    const onWrapperPointerLeave = useCallback(() => {
        if (dragSessionActiveRef.current) return;
        disarmLongPress();
    }, [disarmLongPress]);

    return (
        <motion.div
            ref={wrapperRef}
            className="blocker-kanban-task-drag"
            draggable={longPressArmed || dragSessionActive}
            onDragStart={runDragStart}
            onDragEnd={runDragEnd}
            onPointerDown={onWrapperPointerDown}
            onPointerMove={onWrapperPointerMove}
            onPointerUp={() => {
                if (dragSessionActiveRef.current) return;
                const suppressClick = longPressArmedRef.current;
                disarmLongPress();
                if (suppressClick) suppressNextClickRef.current = true;
            }}
            onPointerCancel={() => {
                if (!dragSessionActiveRef.current) disarmLongPress();
            }}
            onPointerLeave={onWrapperPointerLeave}
            onClickCapture={(e) => {
                if (!suppressNextClickRef.current) return;
                suppressNextClickRef.current = false;
                e.preventDefault();
                e.stopPropagation();
            }}
            style={{
                cursor: longPressArmed ? 'grabbing' : 'default',
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
            }}
            initial={false}
            animate={{
                height: shrinkSource ? 0 : 'auto',
                opacity: shrinkSource ? 0 : 1,
            }}
            transition={{
                height: {
                    duration: KANBAN_SOURCE_SHRINK_DURATION,
                    ease: KANBAN_SOURCE_SHRINK_EASE,
                },
                opacity: {
                    duration: KANBAN_SOURCE_SHRINK_DURATION * 0.45,
                    ease: [0.4, 0, 0.2, 1],
                },
            }}
        >
            {children}
            <span
                data-kanban-task-card-actions
                style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 4,
                }}
            >
                <span
                    role="button"
                    tabIndex={0}
                    aria-label="Drag blocker"
                    data-kanban-drag-handle
                    draggable={Boolean(blockerId)}
                    onDragStart={runDragStart}
                    onDragEnd={runDragEnd}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                    }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                        color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                        boxShadow: '0 0 0 1px var(--ant-color-border-secondary, rgba(0,0,0,0.06))',
                    }}
                >
                    <KanbanDragHandleIcon size={14} />
                </span>
            </span>
        </motion.div>
    );
}
